import { cn } from '@/lib/cn';
import { Fade } from '@heidi/design-system/fade';
import { useVerticalScrollFade } from '@heidi/design-system/hooks/use-vertical-scroll-fade';
import { useReducedMotion } from 'motion/react';
import { type ReactNode, useRef } from 'react';
import { type AnimateOptions, Streamdown } from 'streamdown';

const STREAMING_WORD_FADE: AnimateOptions = {
  animation: 'fadeIn',
  duration: 500,
  easing: 'ease-out',
  sep: 'word',
};

/** Insert keeps single newlines as `<br>`, so the preview must too. */
const withHardBreaks = (text: string): string =>
  text.replace(/([^\n])\n(?=[^\n])/g, '$1  \n');

type MdProps = { children?: ReactNode };

const BLOCK_SPACING = 'my-1 first:mt-0 last:mb-0';

/** The preview is a compact popover, so headings render as bold lines, not document structure. */
const MdHeading = ({ children }: MdProps) => (
  <p className={cn(BLOCK_SPACING, 'font-semibold')}>{children}</p>
);

const inlineEditMdComponents = {
  p: ({ children }: MdProps) => <p className={BLOCK_SPACING}>{children}</p>,
  h1: MdHeading,
  h2: MdHeading,
  h3: MdHeading,
  h4: MdHeading,
  h5: MdHeading,
  h6: MdHeading,
  ul: ({ children }: MdProps) => (
    <ul className={cn(BLOCK_SPACING, 'list-disc ps-4')}>{children}</ul>
  ),
  ol: ({ children }: MdProps) => (
    <ol className={cn(BLOCK_SPACING, 'list-decimal ps-4')}>{children}</ol>
  ),
  li: ({ children }: MdProps) => <li>{children}</li>,
  strong: ({ children }: MdProps) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }: MdProps) => <em>{children}</em>,
};

/**
 * Expandable scroll region for inline edit responses. Mounted for the whole
 * generating/review phase — including before the first chunk — so the
 * `0fr -> 1fr` row transition has a painted starting value to animate from.
 */
export const InlineEditStreamingBody = ({
  text,
  isStreaming,
}: {
  text: string;
  isStreaming: boolean;
}) => {
  const hasText = text.trim().length > 0;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { canScrollUp, canScrollDown } = useVerticalScrollFade(scrollRef, hasText, [
    text,
    isStreaming,
  ]);
  const shouldReduceMotion = useReducedMotion();
  const fadeWords = isStreaming && !shouldReduceMotion;

  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows] duration-300 ease-out-quart motion-reduce:transition-none',
        hasText ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        // Cancels the bar's `gap-2` while collapsed so the empty shell adds no
        // dead space above the loader row.
        !hasText && '-mb-2'
      )}
      data-testid="inline-edit-streaming-shell"
    >
      <div className="min-h-0 overflow-hidden">
        <div className="relative [--fade-bg:hsl(var(--twc-surface))]">
          <div
            ref={scrollRef}
            data-testid="inline-edit-streaming-body"
            className="scrollbar-subtle max-h-48 overflow-y-auto rounded-md text-sm text-text-primary"
            data-streaming={isStreaming ? 'true' : undefined}
          >
            <Streamdown
              parseIncompleteMarkdown={isStreaming}
              animated={fadeWords ? STREAMING_WORD_FADE : undefined}
              isAnimating={isStreaming}
              mode={isStreaming ? undefined : 'static'}
              controls={false}
              components={inlineEditMdComponents}
            >
              {withHardBreaks(text)}
            </Streamdown>
          </div>
          {canScrollUp ? (
            <Fade
              side="top"
              background="var(--fade-bg)"
              className="inset-x-0 top-0 h-6"
              data-testid="inline-edit-scroll-top-fade"
            />
          ) : null}
          {canScrollDown ? (
            <Fade
              side="bottom"
              background="var(--fade-bg)"
              className="inset-x-0 bottom-0 h-6"
              data-testid="inline-edit-scroll-bottom-fade"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
